import { z } from 'zod';
import { TimeZoneSchema } from './tenancy';
import { ConditionRuleSchema } from './workflow/rules';

/** One year in minutes; goals beyond that are almost certainly a unit mistake. */
export const SLA_MAX_MINUTES = 525_600;

/** Response and/or resolution target for one priority, in minutes of calendar time. */
export const SlaGoalSchema = z
  .strictObject({
    responseMinutes: z.int().min(1).max(SLA_MAX_MINUTES).optional(),
    resolutionMinutes: z.int().min(1).max(SLA_MAX_MINUTES).optional(),
  })
  .refine((g) => g.responseMinutes !== undefined || g.resolutionMinutes !== undefined, {
    message: 'a goal needs responseMinutes and/or resolutionMinutes',
  });
export type SlaGoal = z.infer<typeof SlaGoalSchema>;

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const WeekdaySchema = z.enum(WEEKDAYS);
export type Weekday = z.infer<typeof WeekdaySchema>;

const TimeOfDaySchema = z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'HH:mm (24-hour)');

export const SlaHoursSchema = z
  .strictObject({
    start: TimeOfDaySchema,
    end: TimeOfDaySchema,
  })
  .refine((h) => h.start < h.end, { message: 'end must be after start' });
export type SlaHours = z.infer<typeof SlaHoursSchema>;

/**
 * Business-hours calendar. Days missing from `hours` are non-working days; `holidays` are
 * calendar dates in the calendar's time zone. A `null` calendar means 24x7.
 */
export const SlaCalendarSchema = z.strictObject({
  timeZone: TimeZoneSchema,
  hours: z.partialRecord(WeekdaySchema, z.array(SlaHoursSchema).min(1).max(4)),
  holidays: z.array(z.iso.date()).max(100).default([]),
});
export type SlaCalendar = z.infer<typeof SlaCalendarSchema>;

/** Goals keyed by priority id. */
export const SlaGoalsSchema = z.record(z.uuid(), SlaGoalSchema);
export type SlaGoals = z.infer<typeof SlaGoalsSchema>;

/** Timer rules: an array is "any of these matches". Empty start = start at creation. */
const SlaConditionsSchema = z.array(ConditionRuleSchema).max(20);

/** Definition only; timers (`ticket_sla`) run in Bolt 8. */
export const SlaPolicySchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1).max(80),
  goals: SlaGoalsSchema,
  startConditions: SlaConditionsSchema,
  pauseConditions: SlaConditionsSchema,
  stopConditions: SlaConditionsSchema,
  calendar: SlaCalendarSchema.nullable(),
  enabled: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type SlaPolicy = z.infer<typeof SlaPolicySchema>;

export const CreateSlaPolicySchema = z.strictObject({
  name: z.string().min(1).max(80),
  goals: SlaGoalsSchema,
  startConditions: SlaConditionsSchema.default([]),
  pauseConditions: SlaConditionsSchema.default([]),
  stopConditions: SlaConditionsSchema.default([]),
  calendar: SlaCalendarSchema.nullable().default(null),
  enabled: z.boolean().default(true),
});
export type CreateSlaPolicy = z.infer<typeof CreateSlaPolicySchema>;

export const UpdateSlaPolicySchema = z
  .strictObject({
    name: z.string().min(1).max(80),
    goals: SlaGoalsSchema,
    startConditions: SlaConditionsSchema,
    pauseConditions: SlaConditionsSchema,
    stopConditions: SlaConditionsSchema,
    calendar: SlaCalendarSchema.nullable(),
    enabled: z.boolean(),
  })
  .partial();
export type UpdateSlaPolicy = z.infer<typeof UpdateSlaPolicySchema>;

/* ----------------------------- Bolt 8: timers (ticket_sla) ----------------------------- */

export const SLA_KINDS = ['response', 'resolution'] as const;
export const SlaKindSchema = z.enum(SLA_KINDS);
export type SlaKind = z.infer<typeof SlaKindSchema>;

/**
 * Lifecycle of one timer: `running` → `paused` (pause condition) → `running` …; `completed`
 * (stop condition met in time), `breached` (due time passed; `sla.breached` emitted),
 * `cancelled` (policy disabled / ticket moved). Terminal states never change again.
 */
export const SLA_STATES = ['running', 'paused', 'completed', 'breached', 'cancelled'] as const;
export const SlaStateSchema = z.enum(SLA_STATES);
export type SlaState = z.infer<typeof SlaStateSchema>;

/** Item of `GET /v1/tickets/{id}/sla` (S-07-08): one row per (policy, kind). */
export const TicketSlaSchema = z.object({
  id: z.uuid(),
  ticketId: z.uuid(),
  policyId: z.uuid(),
  kind: SlaKindSchema,
  state: SlaStateSchema,
  startedAt: z.iso.datetime(),
  pausedAt: z.iso.datetime().nullable(),
  /** Calendar time consumed while running, accumulated at each pause. */
  elapsedMs: z.int().min(0),
  dueAt: z.iso.datetime().nullable(),
  breachedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  /**
   * Computed at read time: `running` → `dueAt - now` (negative until the sweep records the
   * breach), `paused` → goal minus `elapsedMs`, terminal states → null.
   */
  remainingMs: z.int().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TicketSla = z.infer<typeof TicketSlaSchema>;

export const TicketSlaListResponseSchema = z.object({ items: z.array(TicketSlaSchema) });
export type TicketSlaListResponse = z.infer<typeof TicketSlaListResponseSchema>;
